import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check } from "lucide-react";
import { SUPPORTED_LANGS, LANG_META, DEFAULT_LANG } from "../i18n/config";
import { useLangPrefix, swapLangPrefix } from "../hooks/useLangPrefix";

// flagcdn.com renders crisp, cross-platform country flags (no emoji font dependency).
const flagUrl = (iso) => `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
const flagUrl2x = (iso) => `https://flagcdn.com/w80/${iso.toLowerCase()}.png`;

const Flag = ({ iso, size = 20, className = "" }) => (
  <img
    src={flagUrl(iso)}
    srcSet={`${flagUrl2x(iso)} 2x`}
    alt=""
    width={size}
    height={Math.round((size * 3) / 4)}
    decoding="async"
    className={`inline-block rounded-[2px] shadow-[0_0_0_1px_rgba(31,42,74,0.15)] ${className}`}
  />
);

export const LanguagePicker = ({ variant = "light" }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: currentLang } = useLangPrefix();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const active = currentLang || i18n.language || DEFAULT_LANG;
  const activeMeta = LANG_META[active] || LANG_META[DEFAULT_LANG];

  const handleChange = (lang) => {
    setOpen(false);
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem("i18nextLng", lang);
    } catch (_) {}
    const next = swapLangPrefix(location.pathname, lang);
    navigate(next + location.search + location.hash);
  };

  const isDark = variant === "dark";

  return (
    <div ref={ref} className="relative" data-testid="language-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium tracking-wide transition-colors ${
          isDark
            ? "text-[#FAF7F0]/80 hover:text-[#C9B896]"
            : "text-[#1F2A4A]/80 hover:text-[#C9B896]"
        }`}
        data-testid="lang-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Flag iso={activeMeta.iso} />
        <span className="uppercase tracking-[0.15em]">{activeMeta.short}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-3 min-w-[210px] bg-white border border-[#1F2A4A]/10 rounded-xl shadow-[0_20px_50px_-10px_rgba(31,42,74,0.18)] py-2 z-[60]"
          data-testid="lang-picker-list"
        >
          {SUPPORTED_LANGS.map((code) => {
            const meta = LANG_META[code];
            const isActive = code === active;
            return (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => handleChange(code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? "bg-[#C9B896]/10 text-[#1F2A4A] font-medium"
                      : "text-[#1F2A4A]/85 hover:bg-[#1F2A4A]/5"
                  }`}
                  data-testid={`lang-option-${code}`}
                >
                  <Flag iso={meta.iso} size={26} />
                  <span className="flex-1">{meta.label}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9B896]">
                    {meta.short}
                  </span>
                  {isActive && <Check className="w-3.5 h-3.5 text-[#C9B896]" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
